/**
 * History Transform Functions for Sidewall
 * Transforms API history responses to frontend models
 */

import type { PartDetailsHistoryAPI } from "../api/types";
import type { HistoryEntry } from "../../../../shared/models";
import {
  SPECIAL_POSITION_IDS,
  BURNER_POSITIONS,
  PART_IDS,
} from "../constants/parts";

type HistoryAction =
  | "Installed"
  | "Uninstalled"
  | "Repaired"
  | "Created"
  | "Online"
  | "Offline";

// ============================================
// Helper Functions
// ============================================

/**
 * Determine the action type from API row flags
 *
 * Business logic:
 * - IsReset=true -> "Repaired"
 * - IsDefaultArray=true & PrevParentPartId=null -> "Created" (first task in spares)
 * - IsDefaultArray=true & PrevParentPartId exists -> "Uninstalled" (removed from position)
 * - Otherwise -> "Installed" (placed on position)
 */
const determineAction = (row: PartDetailsHistoryAPI): HistoryAction => {
  if (row.IsReset) {
    return "Repaired";
  }

  if (row.IsDefaultArray) {
    // Check if this is the first task (no previous location) - means part was created
    return row.PrevParentPartId === null ? "Created" : "Uninstalled";
  }

  return "Installed";
};

/**
 * Build a location display string from parent and position names
 *
 * Format: "ParentName - PositionName" or just the available name
 */
const buildLocationDisplay = (
  parentName: string | null,
  positionName: string | null,
): string | null => {
  if (parentName && positionName) {
    return `${parentName} - ${positionName}`;
  }
  return parentName || positionName || null;
};

/**
 * Get the appropriate location name based on action type
 *
 * - For Uninstall: shows where it was uninstalled FROM (previous location)
 * - For Install/Repair: shows where it was installed TO (current location)
 */
const getLocationName = (
  row: PartDetailsHistoryAPI,
  action: HistoryAction,
): string | null => {
  if (action === "Uninstalled" && row.PrevParentPartName) {
    // Show the "from" location for uninstalls
    return buildLocationDisplay(row.PrevParentPartName, row.PrevPositionName);
  }

  // Show the "to" location for installs/repairs
  return buildLocationDisplay(row.ParentPartName, row.PositionName);
};

/**
 * Combine part info fields into a single display string
 *
 * Format: "SerialNumber | Make | Model | Vendor" (filters out nulls/empty)
 * Falls back to PartInfo if individual fields not available
 */
const combinePartInfo = (row: PartDetailsHistoryAPI): string | null => {
  // If we have individual fields, combine them
  if (row.SerialNumber || row.Make || row.Model || row.Vendor) {
    const parts = [row.SerialNumber, row.Make, row.Model, row.Vendor].filter(
      (v) => v !== null && v !== undefined && v !== "",
    );
    return parts.length > 0 ? parts.join(" | ") : null;
  }
  // Fall back to pre-combined PartInfo field
  return row.PartInfo || null;
};

// ============================================
// Filter Functions
// ============================================

/**
 * Shell position IDs to filter out from history.
 * These are install/uninstall operations of shells themselves (Upper=19, Lower=18),
 * not parts installed IN the shells.
 */
const SHELL_POSITION_IDS = new Set<number>([
  SPECIAL_POSITION_IDS.UPPER_SHELL,
  SPECIAL_POSITION_IDS.LOWER_SHELL,
]);

/**
 * BurnerPanel fixture position IDs to filter out from history.
 * BurnerPanels are permanent fixtures at positions 3, 5, 9 on the Upper Shell.
 * Only their sub-components (Block, Front Face, Water Jacket, Nozzle) should appear.
 */
const BURNER_PANEL_POSITION_IDS = new Set<number>(BURNER_POSITIONS);

/**
 * Check if a history row is a shell install/uninstall operation
 * that should be filtered out.
 * Checks PositionId (install target) and PrevPositionId (uninstall source).
 */
const isShellOperation = (row: PartDetailsHistoryAPI): boolean =>
  (row.PositionId !== null && SHELL_POSITION_IDS.has(row.PositionId)) ||
  (row.PrevPositionId !== null && SHELL_POSITION_IDS.has(row.PrevPositionId));

/**
 * Check if a history row is a BurnerPanel fixture install/uninstall operation
 * that should be filtered out.
 * Matches position IDs 3/5/9 only when ParentPartId is the Upper Shell,
 * distinguishing BurnerPanel operations from sub-component operations.
 */
const isBurnerPanelOperation = (row: PartDetailsHistoryAPI): boolean => {
  const isBurnerPanelPos = (
    posId: number | null,
    parentId: number | null,
  ): boolean =>
    posId !== null &&
    BURNER_PANEL_POSITION_IDS.has(posId) &&
    parentId === PART_IDS.UPPER_SHELL;

  return (
    isBurnerPanelPos(row.PositionId, row.ParentPartId) ||
    isBurnerPanelPos(row.PrevPositionId, row.PrevParentPartId)
  );
};

// ============================================
// Main Transform Function
// ============================================

/**
 * Transform API response from GetPartDetails history to frontend HistoryEntry[]
 *
 * @param apiData - Raw history rows from EQP.GetPartDetails
 * @returns Formatted history entries for UI display
 */
export const transformHistoryAPIToEntries = (
  apiData: PartDetailsHistoryAPI[],
): HistoryEntry[] => {
  // Filter out shell and BurnerPanel fixture install/uninstall operations
  const filteredData = apiData.filter(
    (row) => !isShellOperation(row) && !isBurnerPanelOperation(row),
  );

  return filteredData.map((row) => {
    const action = determineAction(row);
    const locationName = getLocationName(row, action);
    const partInfo = combinePartInfo(row);

    return {
      HistoryId: row.TaskId,
      Date: row.Timestamp
        ? row.Timestamp instanceof Date
          ? row.Timestamp
          : new Date(row.Timestamp)
        : null,
      Action: action,
      LocationName: locationName,
      Heats: row.Heats ?? 0,
      User: row.User,
      Notes: row.Notes,
      PartInfo: partInfo,
      PartId: row.PartId ?? null,
      IsReset: row.IsReset ?? false,
      IsDefaultArray: row.IsDefaultArray ?? false,
      PreviousTask: row.PreviousTask ?? null,
    };
  });
};
